import * as THREE from 'three';
import Experience from '../../Experience.js';

export default class ParticleTextBright {
    constructor(initTEXT, distanceFromCamera = 10, particleDensity = 1, initTextAlpha = 1.0) {
        this.experience = new Experience();
        this.scene = this.experience.scene;
        this.time = this.experience.time;
        this.camera = this.experience.camera;
        this.renderer = this.experience.renderer;

        // パーティクルシステム
        this.particleSystem = null;
        this.particles = [];
        this.originalPositions = [];

        // テキスト設定
        this.currentText = initTEXT;
        this.distanceFromCamera = distanceFromCamera; // カメラからの距離
        this.particleDensity = particleDensity; // 1=最高密度, 2=1/2密度, 3=1/3密度...
        this.sparkleSpeed = 1.0; // 煌めきの速度
        this.sparkleIntensity = 1.0; // 煌めきの強度 (0.0～2.0)
        this.textAlpha = initTextAlpha;
        
        // 煌めき範囲の直接制御
        this.scaleMin = 0.6;      // サイズの最小値
        this.scaleMax = 1.4;      // サイズの最大値
        this.brightnessMin = 0.5; // 明度の最小値
        this.brightnessMax = 1.0; // 明度の最大値

        this.init();

        // アニメーションループに登録
        this.time.on('tick', () => {
            this.update();
        });
    }

    init() {
        this.createTextParticles(this.currentText);
    }

    // カメラの前方位置とオリエンテーションを計算
    getCameraFrontTransform() {
        // カメラの位置
        const cameraPosition = this.camera.instance.position.clone();

        // カメラの向いている方向（前方ベクトル）
        const cameraDirection = new THREE.Vector3();
        this.camera.instance.getWorldDirection(cameraDirection);

        // カメラの前方の目標位置
        const targetPosition = cameraPosition.clone().add(
            cameraDirection.multiplyScalar(this.distanceFromCamera)
        );

        // カメラの回転（クォータニオン）
        const cameraQuaternion = new THREE.Quaternion();
        this.camera.instance.getWorldQuaternion(cameraQuaternion);

        return {
            position: targetPosition,
            quaternion: cameraQuaternion,
            direction: cameraDirection.normalize()
        };
    }

    createTextParticles(text) {
        // 既存のパーティクルシステムを削除
        if (this.particleSystem) {
            this.scene.remove(this.particleSystem);
            this.particleSystem.geometry.dispose();
            this.particleSystem.material.dispose();
        }

        // 1. 隠しキャンバスでテキストを描画
        const canvas = document.createElement('canvas');
        /* NOTE : ctx について
        Canvas 2D Rendering Context（キャンバス2D描画コンテキスト）
        ctxは、キャンバスに絵を描くための「絵具セット」のようなものです。
        javascriptで ctxが持つ描画メソッド群
        ctx.fillRect()     // 四角形を塗りつぶす
        ctx.fillText()     // テキストを描画
        ctx.drawImage()    // 画像を描画 など他にも色々あるよ
        */
        const ctx = canvas.getContext('2d');

        // フォント設定
        const fontSize = 80;
        ctx.font = `bold ${fontSize}px Arial`;
        const textWidth = ctx.measureText(text).width;
        const textHeight = fontSize;

        canvas.width = textWidth + 40;
        canvas.height = textHeight + 40;

        // テキスト描画
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = '#ffffff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        // 2. ピクセルデータ取得
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;  // RGBAピクセル配列

        // 3. カメラの前方位置とオリエンテーションを取得
        const cameraTransform = this.getCameraFrontTransform();

        // 4. パーティクル用の配列を初期化
        const positions = [];
        const colors = [];
        const scales = [];
        const randomOffsets = []; // 各パーティクルのランダムタイミング
        const originalPos = [];

        // パーティクル密度を設定（数値が大きいほど粗くなる）
        const step = Math.max(1, Math.floor(this.particleDensity));

        for (let y = 0; y < canvas.height; y += step) {
            for (let x = 0; x < canvas.width; x += step) {
                const pixelIndex = (y * canvas.width + x) * 4;
                const alpha = data[pixelIndex + 3];

                if (alpha > 128) {
                    // ローカル座標でのテキスト位置（カメラ正面を基準）
                    const localX = (x - canvas.width / 2) * 0.05;
                    const localY = (canvas.height / 2 - y) * 0.05;
                    // 奥行き（厚み）を1/5に（5→1)
                    const localZ = (Math.random() - 0.5) * 1;

                    // ローカル座標をベクトルに変換
                    const localPosition = new THREE.Vector3(localX, localY, localZ);

                    // カメラの回転を適用してワールド座標に変換
                    localPosition.applyQuaternion(cameraTransform.quaternion);

                    // カメラの前方位置に移動
                    const worldPosition = localPosition.add(cameraTransform.position);

                    positions.push(worldPosition.x, worldPosition.y, worldPosition.z);
                    originalPos.push(worldPosition.x, worldPosition.y, worldPosition.z);

                    // ランダムな色（より鮮やかに）
                    const hue = Math.random();
                    const color = new THREE.Color().setHSL(hue, 0.9, 0.7); // 彩度と明度を上げる
                    colors.push(color.r, color.g, color.b);

                    // ランダムなスケール（より大きなバリエーション）
                    const baseScale = Math.random() * 1.2 + 0.4;
                    scales.push(baseScale);

                    // 各パーティクルに独立したランダムタイミングオフセットを追加
                    randomOffsets.push(Math.random() * Math.PI * 2);
                }
            }
        }

        // 自動削除タイマー（ループの外に移動）
        setTimeout(() => {
            this.destroy();
        }, 10000);

        // ジオメトリ作成
        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
        geometry.setAttribute('scale', new THREE.Float32BufferAttribute(scales, 1));
        geometry.setAttribute('randomOffset', new THREE.Float32BufferAttribute(randomOffsets, 1));

        // マテリアル作成（煌めき効果付き）
        this.material = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                sparkleIntensity: { value: this.sparkleIntensity },
                scaleMin: { value: this.scaleMin },
                scaleMax: { value: this.scaleMax },
                brightnessMin: { value: this.brightnessMin },
                brightnessMax: { value: this.brightnessMax },
                textAlpha: {value: this.textAlpha}
            },
            vertexShader: `
                attribute float scale;
                attribute float randomOffset;
                varying vec3 vColor;
                varying float vScale;
                varying float vRandomOffset;
                uniform float time;
                uniform float sparkleIntensity;
                uniform float scaleMin;
                uniform float scaleMax;

                void main() {
                    vColor = color;
                    vScale = scale;
                    vRandomOffset = randomOffset;

                    vec3 pos = position;

                    // 微細な浮遊効果（煌めきに合わせて調整）
                    pos.z += sin(time * 1.5 + randomOffset + pos.x * 0.05 + pos.y * 0.05) * 0.3;
                    pos.y += cos(time * 1.2 + randomOffset + pos.x * 0.03) * 0.1;
                    pos.x += sin(time * 0.8 + randomOffset + pos.y * 0.03) * 0.08;

                    // サイズ変動を直接制御
                    float range = scaleMax - scaleMin;
                    float twinkleScale = scaleMin + (sin(time * 3.5 + randomOffset) * 0.5 + 0.5) * range;
                    float flashEffect = scaleMin + (sin(time * 7.0 + randomOffset * 2.5) * 0.5 + 0.5) * range * 0.5;
                    
                    // 最終的なスケールは2つの効果を組み合わせ
                    float finalScale = (twinkleScale + flashEffect) * 0.5;
                    
                    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                    gl_PointSize = scale * finalScale * 0.4 * (300.0 / -mvPosition.z);
                    gl_Position = projectionMatrix * mvPosition;
                }
            `,
            fragmentShader: `
                varying vec3 vColor;
                varying float vScale;
                varying float vRandomOffset;
                uniform float time;
                uniform float sparkleIntensity;
                uniform float brightnessMin;
                uniform float brightnessMax;
                uniform float textAlpha;

                void main() {
                    vec2 center = vec2(0.5);
                    float dist = distance(gl_PointCoord, center);

                    if (dist > 0.5) discard;

                    float intensity = 1.0 - smoothstep(0.0, 0.5, dist);
                    intensity = pow(intensity, 1.8);

                    // 明度変動を直接制御
                    float brightnessRange = brightnessMax - brightnessMin;
                    float twinkle1 = brightnessMin + (sin(time * 4.5 + vRandomOffset) * 0.5 + 0.5) * brightnessRange;
                    float twinkle2 = brightnessMin + (sin(time * 6.5 + vRandomOffset * 1.7) * 0.5 + 0.5) * brightnessRange * 0.6;
                    float flash = brightnessMin + (sin(time * 11.0 + vRandomOffset * 2.8) * 0.5 + 0.5) * brightnessRange * 0.4;
                    
                    // 煌めきパターンの組み合わせ
                    float sparkle = (twinkle1 + twinkle2 + flash) / 3.0;
                    
                    // 星のような輝きパターン（テキスト用に調整）
                    float starPattern = max(
                        smoothstep(0.4, 0.5, abs(gl_PointCoord.x - 0.5)) * smoothstep(0.15, 0.0, abs(gl_PointCoord.y - 0.5)),
                        smoothstep(0.4, 0.5, abs(gl_PointCoord.y - 0.5)) * smoothstep(0.15, 0.0, abs(gl_PointCoord.x - 0.5))
                    );
                    
                    intensity += starPattern * sparkle * 0.9;

                    // テキスト用の基本明度調整
                    float brightness = 0.6 + 0.4 * vScale;
                    
                    // 既存のtwinkle効果も保持
                    float originalTwinkle = sin(gl_PointCoord.x * 20.0) * sin(gl_PointCoord.y * 20.0);
                    intensity += originalTwinkle * 0.1 * sparkle;

                    // 色の強化
                    vec3 enhancedColor = vColor;
                    
                    // 明度を直接制御された範囲で調整
                    enhancedColor = enhancedColor * sparkle + vec3(0.1 * (brightnessMax - 0.5));
                    
                    // 彩度の強化
                    vec3 luminance = vec3(0.299, 0.587, 0.114);
                    float lum = dot(enhancedColor, luminance);
                    enhancedColor = mix(vec3(lum), enhancedColor, 1.0 + 0.4 * sparkleIntensity);

                    vec3 glowColor = enhancedColor * brightness;
                    
                    // 輝きの中心部をより明るく
                    float centerGlow = 1.0 - smoothstep(0.0, 0.25, dist);
                    glowColor += vec3(0.3 * brightnessMax) * centerGlow * sparkle;

                    gl_FragColor = vec4(glowColor, intensity * 0.95 * textAlpha);
                }
            `,
            transparent: true,
            vertexColors: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });

        // パーティクルシステム作成
        this.particleSystem = new THREE.Points(geometry, this.material);
        this.scene.add(this.particleSystem);

        // 元の位置を保存
        this.originalPositions = originalPos;

        console.log(`Created ${positions.length / 3} particles for text: ${text} (density: 1/${this.particleDensity}) at camera front`);
    }

    update() {
        if (!this.particleSystem) return;

        // シェーダーのuniformを更新
        this.particleSystem.material.uniforms.time.value = this.time.elapsed * 0.001 * this.sparkleSpeed;
        this.particleSystem.material.uniforms.sparkleIntensity.value = this.sparkleIntensity;
        this.particleSystem.material.uniforms.scaleMin.value = this.scaleMin;
        this.particleSystem.material.uniforms.scaleMax.value = this.scaleMax;
        this.particleSystem.material.uniforms.brightnessMin.value = this.brightnessMin;
        this.particleSystem.material.uniforms.brightnessMax.value = this.brightnessMax;

        // 軽微な回転アニメーション（削除するかオプションにする）
        // this.particleSystem.rotation.y = Math.sin(this.time.elapsed * 0.0005) * 0.1;
    }

    // パーティクル密度を設定（1=最高密度, 2=1/2密度, 3=1/3密度...）
    setParticleDensity(density) {
        this.particleDensity = Math.max(1, density);
        // 現在のテキストを再描画
        if (this.currentText) {
            this.createTextParticles(this.currentText);
        }
    }

    // 煌めきの速度を設定
    setSparkleSpeed(speed) {
        this.sparkleSpeed = Math.max(0.1, Math.min(5, speed));
    }

    // サイズ変動範囲を直接設定（min: 最小倍率, max: 最大倍率）
    setScaleRange(min, max) {
        this.scaleMin = Math.max(0.1, min);
        this.scaleMax = Math.max(this.scaleMin + 0.1, max);
        
        if (this.particleSystem) {
            this.particleSystem.material.uniforms.scaleMin.value = this.scaleMin;
            this.particleSystem.material.uniforms.scaleMax.value = this.scaleMax;
        }
    }

    // 明度変動範囲を直接設定（min: 最小明度, max: 最大明度）
    setBrightnessRange(min, max) {
        this.brightnessMin = Math.max(0.0, Math.min(1.0, min));
        this.brightnessMax = Math.max(this.brightnessMin + 0.1, Math.min(1.0, max));
        
        if (this.particleSystem) {
            this.particleSystem.material.uniforms.brightnessMin.value = this.brightnessMin;
            this.particleSystem.material.uniforms.brightnessMax.value = this.brightnessMax;
        }
    }

    // 常に明るく、サイズも安定した設定にするプリセット
    setBrightStableMode() {
        this.setScaleRange(1.2, 1.5);     // サイズは1.2～1.5倍で変動
        this.setBrightnessRange(0.8, 1.0); // 明度は0.8～1.0で常に明るく
        this.setSparkleSpeed(0.8);          // ゆったりとした煌めき
    }

    // 控えめな煌めきプリセット
    setSubtleMode() {
        this.setScaleRange(0.9, 1.1);     // サイズは0.9～1.1倍で控えめ
        this.setBrightnessRange(0.6, 0.9); // 明度は0.6～0.9
        this.setSparkleSpeed(0.5);          // 遅い煌めき
    }

    // 激しい煌めきプリセット
    setIntenseMode() {
        this.setScaleRange(0.3, 2.0);     // サイズは0.3～2.0倍で激しく変動
        this.setBrightnessRange(0.1, 1.0); // 明度も大きく変動
        this.setSparkleSpeed(2.0);          // 高速煌めき
    }

    // 煌めき効果の強度を設定（0.0=煌めきなし, 1.0=標準, 2.0=強烈）
    setSparkleIntensity(intensity) {
        this.sparkleIntensity = Math.max(0, Math.min(2, intensity));
        if (this.particleSystem && this.particleSystem.material.uniforms.sparkleIntensity) {
            this.particleSystem.material.uniforms.sparkleIntensity.value = this.sparkleIntensity;
        }
    }

    // カメラ位置が変わった時にテキストを再配置するメソッド
    updateToCurrentCameraPosition() {
        if (this.currentText) {
            this.createTextParticles(this.currentText);
        }
    }

    // カメラからの距離を変更
    setDistanceFromCamera(newDistance) {
        this.distanceFromCamera = newDistance;
        this.updateToCurrentCameraPosition();
    }

    // テキスト変更メソッド
    changeText(newText) {
        this.currentText = newText;
        this.createTextParticles(newText);
    }

    // パーティクルシステムを削除
    destroy() {
        if (this.particleSystem) {
            this.scene.remove(this.particleSystem);
            this.particleSystem.geometry.dispose();
            this.particleSystem.material.dispose();
        }
    }
}